import { Fragment } from "react";
import { Listbox, ListboxButton, ListboxOption, ListboxOptions, Transition } from "@headlessui/react";
import { CheckIcon, ChevronDownIcon } from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";

import classNames from "../../../utils/classNames";

export interface OnPrimarySelectOption {
  id: any;
  name: string;
}

interface OnPrimarySelectProps {
  /** Label shown on the left of the control. */
  label: string;
  options: OnPrimarySelectOption[];
  value: any;
  onChange: (value: any) => void;
  icon?: React.ReactNode;
  placeholder?: string;
  disabled?: boolean;
  classes?: string;
}

/**
 * Select designed to sit on a primary-coloured surface. The label sits inline on
 * the left and the current value (or {@link placeholder}) renders as a badge on
 * the right, matching {@link OnPrimaryButton}.
 */
export default function OnPrimarySelect({
  label,
  options,
  value,
  onChange,
  icon,
  placeholder = "Not set",
  disabled = false,
  classes = "",
}: OnPrimarySelectProps) {
  const { t } = useTranslation("common");
  const selectedOption = options.find((o) => o.id === value);

  return (
    <Listbox value={value} onChange={onChange} disabled={disabled}>
      {({ open }) => (
        <div className={classNames("relative w-full", classes)}>
          <ListboxButton
            className={classNames(
              "flex w-full items-center gap-2 rounded-md border border-white/25 bg-white/10 px-3 py-2.5 text-left text-sm font-medium text-white transition-colors",
              "hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/60 disabled:cursor-not-allowed disabled:opacity-70"
            )}
          >
            {icon && <span className="flex h-4 w-4 shrink-0 items-center justify-center">{icon}</span>}
            <span className="flex-1 truncate">{label}</span>

            <span className="rounded bg-white/15 px-2 py-0.5 text-xs font-medium text-white">
              {selectedOption?.name ?? t(placeholder)}
            </span>
            <ChevronDownIcon
              className={classNames("h-4 w-4 shrink-0 text-white/70 transition-transform", open ? "rotate-180" : "")}
              aria-hidden="true"
            />
          </ListboxButton>

          <Transition
            show={open}
            appear
            as={Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <ListboxOptions className="absolute z-20 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
              {options.map((option) => (
                <ListboxOption
                  key={option.id}
                  value={option.id}
                  className={({ active }) =>
                    classNames(
                      active ? "bg-primary-100 text-gray-900" : "text-gray-900",
                      "relative cursor-pointer select-none py-2 pl-8 pr-4"
                    )
                  }
                >
                  <span className={classNames(value === option.id ? "font-semibold" : "font-normal", "block truncate")}>
                    {option.name}
                  </span>
                  {value === option.id ? (
                    <span className="absolute inset-y-0 left-0 flex items-center pl-1.5 text-primary-600">
                      <CheckIcon className="h-5 w-5" aria-hidden="true" />
                    </span>
                  ) : null}
                </ListboxOption>
              ))}
            </ListboxOptions>
          </Transition>
        </div>
      )}
    </Listbox>
  );
}
